/**
 * Questionnaire Service
 *
 * Handles all API calls related to the AI Readiness Questionnaire:
 * - Fetching questionnaire structure (versions, layers, categories, questions)
 * - Submitting assessment responses
 * - Retrieving assessment results
 */

import { httpClient } from "../../api/http-client.js";

const BASE_PATH = "/questionnaire";

export interface QuestionnaireVersion {
  id: string;
  version_number: string;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface QuestionnaireLayer {
  id: string;
  version_id: string;
  layer_number: number;
  name: string;
  description: string;
  weight_percentage: number;
  interpretation_notes: string;
  display_order: number;
  created_at: string;
}

export interface QuestionnaireCategory {
  id: string;
  layer_id: string;
  category_number: string;
  name: string;
  description: string;
  weight_percentage: number;
  display_order: number;
  created_at: string;
}

export interface QuestionnaireQuestion {
  id: string;
  category_id: string;
  question_number: string;
  question_text: string;
  example_text: string | null;
  display_order: number;
  is_required: boolean;
  created_at: string;
}

export interface QuestionnaireStructure {
  version: QuestionnaireVersion;
  layers: QuestionnaireLayer[];
  categories: QuestionnaireCategory[];
  questions: QuestionnaireQuestion[];
}

export interface AssessmentResponse {
  question_id: string;
  response_value: number;
  context_text?: string;
}

export interface AssessmentSession {
  id: string;
  version_id: string;
  user_id?: string | null;
  organization_id?: string;
  session_started_at: string;
  session_completed_at?: string;
  is_complete: boolean;
}

export interface AssessmentResult {
  id: string;
  session_id: string;
  overall_score: number;
  structural_readiness_score: number;
  investment_leverage_score: number;
  category_scores: Record<string, number>;
  identified_problems: string[];
  recommended_solutions: string[];
  created_at: string;
}

class QuestionnaireService {
  /**
   * Fetch the active questionnaire structure
   */
  async getActiveQuestionnaire(): Promise<QuestionnaireStructure> {
    return httpClient.get<QuestionnaireStructure>(`${BASE_PATH}/active`);
  }

  /**
   * Get a specific questionnaire version by ID
   */
  async getQuestionnaireVersion(
    versionId: string,
  ): Promise<QuestionnaireStructure> {
    return httpClient.get<QuestionnaireStructure>(
      `${BASE_PATH}/versions/${versionId}`,
    );
  }

  /**
   * Start a new assessment session
   */
  async startAssessmentSession(data: {
    version_id: string;
    user_id?: string;
    organization_id?: string;
  }): Promise<AssessmentSession> {
    return httpClient.post<AssessmentSession>(`${BASE_PATH}/sessions`, data);
  }

  /**
   * Save assessment responses (can be called multiple times for progress saving)
   */
  async saveResponses(
    sessionId: string,
    responses: AssessmentResponse[],
  ): Promise<void> {
    return httpClient.post(`${BASE_PATH}/sessions/${sessionId}/responses`, {
      responses,
    });
  }

  /**
   * Submit complete assessment and generate results
   */
  async submitAssessment(
    sessionId: string,
    responses: AssessmentResponse[],
  ): Promise<AssessmentResult> {
    return httpClient.post<AssessmentResult>(
      `${BASE_PATH}/sessions/${sessionId}/submit`,
      { responses },
    );
  }

  /**
   * Get assessment results for a completed session
   */
  async getAssessmentResults(sessionId: string): Promise<AssessmentResult> {
    return httpClient.get<AssessmentResult>(
      `${BASE_PATH}/sessions/${sessionId}/results`,
    );
  }

  /**
   * Get previous assessment sessions for a user/organization
   */
  async getAssessmentHistory(
    userId?: string,
    organizationId?: string,
  ): Promise<AssessmentSession[]> {
    const params = new URLSearchParams();
    if (userId) params.append("user_id", userId);
    if (organizationId) params.append("organization_id", organizationId);

    const queryString = params.toString();
    const url = queryString
      ? `${BASE_PATH}/sessions?${queryString}`
      : `${BASE_PATH}/sessions`;
    return httpClient.get<AssessmentSession[]>(url);
  }

  /**
   * Get all assessment sessions for a specific user
   */
  async getUserSessions(userId: string): Promise<AssessmentSession[]> {
    return httpClient.get<AssessmentSession[]>(
      `${BASE_PATH}/user-sessions/${userId}`,
    );
  }
}

export const questionnaireService = new QuestionnaireService();
